import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ENGAGEMENT_SCALE_TYPE_OPTIONS, OPEN_ENGAGEMENT_QUESTION_PANEL } from '../constants/AddQuestionConstant';

export type EngagementAnswerType = 'EMOJI' | 'NUMBER_RATING' | 'DROP_DOWN' | 'NONE';

export type EngagementAnswerOptions = {
  score: number;
  displayOption: number;
  description: string;
  isDisabled?: boolean;
}[];

export type EngagementScaleType =
  | 'AGREE_DISAGREE'
  | 'NEVER_ALWAYS'
  | 'DISSATISFIED_SATISFIED'
  | 'UNLIKELY_LIKELY'
  | 'POOR_EXCELLENT'
  | 'UNIMPORTANT_IMPORTANT'
  | 'NOT_CONFIDENT_CONFIDENT'
  | 'EASY_DIFFICULT'
  | 'NO_UNDERSTANDING_FULL_UNDERSTANDING';

export type EngagementQuestionType = {
  id?: number;
  answerType: EngagementAnswerType;
  description: string;
  enableFeedbackText: boolean;
  isTextRequired: boolean;
  questionType: 'ENGAGEMENT';
  analyticsQuestionType: 'RATING';
  tag?: {
    uuid: string;
  };
  minOptions?: number;
  maxOptions?: number;
  answerOptions?: EngagementAnswerOptions;
  scaleType?: EngagementScaleType;
  // weird properties
  isArchived: boolean;
  isArchive: boolean;
  isEditable?: boolean;
  isPredefined?: boolean;
};

type AddQuestionInitialState = {
  question: EngagementQuestionType;
};

const initialState: AddQuestionInitialState = {
  question: {
    answerType: 'EMOJI',
    description: '',
    enableFeedbackText: false,
    isTextRequired: false,
    questionType: 'ENGAGEMENT',
    analyticsQuestionType: 'RATING',
    tag: null,
    minOptions: null,
    maxOptions: 3,
    answerOptions: ENGAGEMENT_SCALE_TYPE_OPTIONS.AGREE_DISAGREE[3],
    scaleType: 'AGREE_DISAGREE',
    isArchived: false,
    isArchive: false,
    isEditable: true,
  },
};

const addEngagementQuestionSlice = createSlice({
  name: 'addEngagementQuestion',
  initialState,
  reducers: {
    updateEngagementAddQuestion: (state, action: PayloadAction<Partial<EngagementQuestionType>>) => ({
      ...state,
      question: {
        ...state.question,
        ...action.payload,
      },
    }),
    initializeEngagementAddQuestion: (state, action?: PayloadAction<Partial<EngagementQuestionType>>) => ({
      ...state,
      question: action?.payload ? { ...state.question, ...action.payload } : initialState.question,
    }),
  },
});

export const { initializeEngagementAddQuestion, updateEngagementAddQuestion } = addEngagementQuestionSlice.actions;
export default addEngagementQuestionSlice.reducer;

export const useIsAddQuestionDrawerVisible = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  return {
    isDrawerOpen: queryParams.get(OPEN_ENGAGEMENT_QUESTION_PANEL) === 'true',
    action: queryParams.get('action') as 'edit' | 'add' | 'copy',
    questionId: queryParams.get('questionId') as string | null,
  };
};

export const useAddEngagementQuestion = () =>
  useSelector((state: { addEngagementQuestion: AddQuestionInitialState }) => state.addEngagementQuestion.question);
